import { useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LayoutTemplate, ChevronDown, Search, Users, User, Loader2 } from "lucide-react";

// Fields a template carries; links, notes, dates and recurrence are deliberately excluded.
export const templateFromTask = (form) => ({
  title: form.title || "",
  activity_type: form.activity_type || "Task",
  priority: form.priority || "Medium",
  assigned_to_ids: form.assigned_to_ids || [],
  subtasks: (form.subtasks || []).filter((s) => s.title?.trim()).map((s) => ({ title: s.title.trim(), assigned_to_id: s.assigned_to_id || null })),
});

export const applyTemplate = (tpl, form) => ({
  ...form,
  title: tpl.title || form.title,
  activity_type: tpl.activity_type || "Task",
  priority: tpl.priority || "Medium",
  assigned_to_ids: tpl.assigned_to_ids?.length ? tpl.assigned_to_ids : form.assigned_to_ids,
  subtasks: (tpl.subtasks || []).map((s) => ({ title: s.title, assigned_to_id: s.assigned_to_id || null, completed: false })),
});

export function VisibilityToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
      {[["me", "Just for me", User], ["everyone", "Everyone", Users]].map(([v, l, Icon]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`template-visibility-${v}`}
          className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors", value === v ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:text-slate-800")}>
          <Icon className="h-3.5 w-3.5" />{l}
        </button>
      ))}
    </div>
  );
}

export function SaveTemplateDialog({ form, onClose }) {
  const [name, setName] = useState(form.title || "");
  const [visibility, setVisibility] = useState("me");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Give the template a name");
    setBusy(true);
    try { await client.post("/task-templates", { name: name.trim(), visibility, ...templateFromTask(form) }); toast.success("Template saved"); onClose(); }
    catch (e) { toast.error("Could not save template"); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription>Saves the task name, type, priority, assignees and sub-tasks. Links, notes and dates aren't saved.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-600">Template name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="template-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-600">Who can use it</label>
            <VisibilityToggle value={visibility} onChange={setVisibility} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="template-save-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="template-save-submit">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatePicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open || list) return;
    client.get("/task-templates").then(({ data }) => setList(data)).catch(() => setList([]));
  }, [open, list]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const shown = useMemo(() => (list || []).filter((t) => !q.trim() || `${t.name} ${t.title}`.toLowerCase().includes(q.trim().toLowerCase())), [list, q]);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="task-template-picker"
        className="flex items-center gap-1.5 rounded-lg border border-dashed border-sky-300 bg-sky-50/50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50">
        <LayoutTemplate className="h-3.5 w-3.5" />Use template<ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded-xl border border-slate-200 bg-white p-2 shadow-xl" data-testid="task-template-menu">
          <div className="relative mb-1.5">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" className="h-8 pl-8 text-sm" autoFocus data-testid="task-template-search" />
          </div>
          <div className="max-h-64 overflow-auto">
            {list === null ? <p className="py-3 text-center text-xs text-slate-400">Loading…</p> : !shown.length ? (
              <p className="py-3 text-center text-xs text-slate-400" data-testid="task-template-empty">{list.length ? "No matches" : "No templates yet — use \"Save as template\" on a task"}</p>
            ) : shown.map((t) => (
              <button key={t.id} type="button" onClick={() => { onPick(t); setOpen(false); }} data-testid={`task-template-option-${t.id}`}
                className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
                {t.visibility === "everyone" ? <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" /> : <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-800">{t.name}</span>
                  <span className="block truncate text-[11px] text-slate-400">{[t.title, t.subtasks?.length ? `${t.subtasks.length} sub-tasks` : null, t.visibility === "everyone" ? `by ${t.owner_name?.split(" ")[0] || "—"}` : "Just me"].filter(Boolean).join(" · ")}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
